import {
  Button,
  ButtonSize,
  ButtonTheme,
  ButtonType,
  Color,
  EmptyPlaceholder,
  FontWeight,
  Input,
  SwitchFrame,
  TextButton,
  TextButtonTheme,
  Typography,
  TypographyVariant,
} from '@elearning-platform/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { TransProps, useTranslation } from 'react-i18next';

import { useAuth } from '../../../hooks';
import { AddonConfig, FormAnswers, UseAuth } from '../../../interfaces';

import { addons } from './form.consts';
import { schema } from './form.schema';
import { ButtonsContainer, Container, FormContainer, FormInnerWrapper, FullWidthField, Row, Section } from './form.styled';
import { ControllerRender, FormProps } from './form.types';

export const Form: React.FC<FormProps> = (props: FormProps): JSX.Element => {
  const { isFormLoading, initialData, onSubmit, addonsConfig, isSubmitting, onClose }: FormProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { userData }: UseAuth = useAuth();

  const { control, handleSubmit, formState: { errors }, reset }: UseFormReturn<FormAnswers> = useForm<FormAnswers>({
    defaultValues: {
      supervisorData: {
        name: initialData?.supervisorName ?? '',
        surname: initialData?.supervisorSurname ?? '',
        email: initialData?.supervisorEmail ?? '',
      },
      userData: {
        phone: initialData?.phone ?? userData?.phone ?? '',
        addons: {
          withAccommodation: initialData?.accommodation ?? false,
          withParking: initialData?.parking ?? false,
          withDinner: initialData?.dinner ?? false,
          withVege: initialData?.vegeFood ?? false,
        },
      },
    },
    resolver: yupResolver(schema),
  });

  useEffect((): void => {
    if (initialData && Object.keys(initialData).length > 0) {
      reset({
        supervisorData: {
          name: initialData.supervisorName ?? '',
          surname: initialData.supervisorSurname ?? '',
          email: initialData.supervisorEmail ?? '',
        },
        userData: {
          phone: initialData.phone ?? userData?.phone ?? '',
          addons: {
            withAccommodation: initialData.accommodation ?? false,
            withParking: initialData.parking ?? false,
            withDinner: initialData.dinner ?? false,
            withVege: initialData.vegeFood ?? false,
          },
        },
      });
    }
  }, [initialData, reset, userData?.phone]);

  return (
    <Container>
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <FormInnerWrapper>
          {isFormLoading ? (
            <EmptyPlaceholder
              colorsData={{ bgColor: Color.White }}
              isLoading
              text={t('competence.app.eventFormDrawer.formLoading')}
            />
          ) : (
            <>
              <Section>
                <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                  {t('competence.app.eventFormDrawer.supervisorData')}
                </Typography>
                <Row>
                  <Controller
                    control={control}
                    name='supervisorData.name'
                    render={({ field }: ControllerRender<'supervisorData.name'>): JSX.Element => (
                      <Input
                        {...field}
                        description={errors.supervisorData?.name?.message}
                        id='supervisorData.name'
                        label={t('competence.app.eventFormDrawer.name')}
                        withError={!!errors.supervisorData?.name?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name='supervisorData.surname'
                    render={({ field }: ControllerRender<'supervisorData.surname'>): JSX.Element => (
                      <Input
                        {...field}
                        description={errors.supervisorData?.surname?.message}
                        id='supervisorData.surname'
                        label={t('competence.app.eventFormDrawer.surname')}
                        withError={!!errors.supervisorData?.surname?.message}
                      />
                    )}
                  />
                  <FullWidthField>
                    <Controller
                      control={control}
                      name='supervisorData.email'
                      render={({ field }: ControllerRender<'supervisorData.email'>): JSX.Element => (
                        <Input
                          {...field}
                          description={errors.supervisorData?.email?.message}
                          id='email'
                          label={t('competence.app.eventFormDrawer.email')}
                          withError={!!errors.supervisorData?.email?.message}
                        />
                      )}
                    />
                  </FullWidthField>
                </Row>
              </Section>
              <Section>
                <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                  {t('competence.app.eventFormDrawer.yourData')}
                </Typography>
                <Row>
                  <Input
                    disabled
                    id='userData.email'
                    label={t('competence.app.eventFormDrawer.email')}
                    value={userData?.email || ''}
                  />
                  <Controller
                    control={control}
                    name='userData.phone'
                    render={({ field }: ControllerRender<'userData.phone'>): JSX.Element => (
                      <Input
                        {...field}
                        description={errors.userData?.phone?.message}
                        id='userPhone'
                        label={t('competence.app.eventFormDrawer.phone')}
                        leftElement='+48'
                        leftElementWidth={36}
                        withError={!!errors.userData?.phone?.message}
                      />
                    )}
                  />
                </Row>
              </Section>
              {!!Object.values(addonsConfig).filter(Boolean).length && (
                <Section>
                  <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                    {t('competence.app.eventFormDrawer.addons')}
                  </Typography>
                  <Row>
                    {addons
                      .filter((addon: AddonConfig): boolean => addonsConfig[addon.key])
                      .map((addon: AddonConfig): JSX.Element => (
                        <Controller
                          control={control}
                          key={addon.key}
                          name={`userData.addons.${addon.key}`}
                          render={({ field }: ControllerRender<`userData.addons.${AddonConfig['key']}`>): JSX.Element => (
                            <SwitchFrame
                              id={`userData.addons.${addon.key}`}
                              isSmallVariant
                              onToggle={(): void => field.onChange(!field.value)}
                              shape={addon.shape}
                              text={t(addon.label)}
                              toggled={field.value}
                            />
                          )}
                        />
                      ))}
                  </Row>
                </Section>
              )}
            </>
          )}
        </FormInnerWrapper>
        <ButtonsContainer>
          <Button
            isLoading={isSubmitting}
            label={t('competence.app.global.save')}
            size={ButtonSize.Medium}
            type={ButtonType.Submit}
            variant={ButtonTheme.PrimaryBrand}
          />
          <TextButton label={t('competence.app.global.cancel')} onClick={onClose} variant={TextButtonTheme.TertiaryGrey} />
        </ButtonsContainer>
      </FormContainer>
    </Container>
  );
};
